import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../../components/ui/select';
import { Input } from '../../../../../../../components/ui/input';
import { Button } from '../../../../../../../components/ui/button';
import { Badge } from '../../../../../../../components/ui/badge';
import { AlertCircle, Edit3, Loader2 } from 'lucide-react';
import { renamePass } from '../../../../../../../redux/device/actions';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../../../../components/ui/table';
import Notification from '../../../../../../../components/ui/custom-notification';

interface RenamePassProps {
    jobData: any;
    projectId?: number;
    pipeParamKey?: string;
    onRenameSuccess?: () => void;
}

interface PassData {
    PassName: string;
    PassPendName: string;
    PassEnable: string;
    PassGroupNumber: string;
    EndingAngle: string;
    Type: string;
    Torch?: string;
    originalIndex?: number;
    [key: string]: any;
}

interface RenameEntry {
    groupNo: string;
    torch: string;
    passName: string;
    newPassName: string;
    numberToAppend: string;
}

const RenamePass: React.FC<RenamePassProps> = ({ jobData, projectId, pipeParamKey, onRenameSuccess }) => {
    const dispatch = useDispatch();
    const [renameEntries, setRenameEntries] = useState<Map<string, RenameEntry>>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [validationError, setValidationError] = useState<string>('');
    const [missingFields, setMissingFields] = useState<Set<string>>(new Set());

    // Store timeout reference for cleanup
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Reset rename entries and loading when job data or pipe param key changes
    React.useEffect(() => {
        setRenameEntries(new Map());
        setIsLoading(false);
        // Clear any pending timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [jobData?.job_number, pipeParamKey]);

    // Cleanup timeout on unmount
    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Pass name options for dropdowns
    const passNameOptions = [
        { value: 'Hot', label: 'Hot' },
        { value: 'Root', label: 'Root' },
        { value: 'Fill', label: 'Fill' },
        { value: 'Last Fill', label: 'Last Fill' },
        { value: 'Strip', label: 'Strip' },
        { value: 'Cap', label: 'Cap' },
        { value: 'Disabled', label: 'Disabled' },
        { value: 'Weave', label: 'Weave' },
    ];

    // Extract all PASS type entries from parsed_json_data or WELD PARAMETERS
    const passEntries = useMemo(() => {
        if (!jobData?.parsed_json_data) return [];
       // console.log('P625 RenamePass - jobData.parsed_json_data:', jobData.parsed_json_data);

        // Check if parsed_json_data is an array
        if (Array.isArray(jobData.parsed_json_data)) {
            // Try to get PASS type entries first
            const passTypeEntries = jobData.parsed_json_data
                .map((item: any, index: number) => ({ ...item, originalIndex: item.originalIndex ?? index }))
                .filter((item: any) => item.Type === 'PASS');
            if (passTypeEntries.length > 0) {
                return passTypeEntries;
            }
        }

        // Extract from WELD PARAMETERS structure (when parsed_json_data is an object)
        const params: PassData[] = [];
        if (typeof jobData.parsed_json_data === 'object' && jobData.parsed_json_data !== null && !Array.isArray(jobData.parsed_json_data)) {
            // Filter by provided pipeParamKey, or find all WELD PARAMETERS keys
            let weldParamKeys: string[] = [];
            if (pipeParamKey) {
                // Use the provided weld parameter key
                if (jobData.parsed_json_data[pipeParamKey]) {
                    weldParamKeys = [pipeParamKey];
                }
            } else {
                // Fallback: find all WELD PARAMETERS keys
                weldParamKeys = Object.keys(jobData.parsed_json_data).filter(key => key.includes('WELD PARAMETERS'));
            }

            for (const currentWeldParamKey of weldParamKeys) {
                const weldParams = jobData.parsed_json_data[currentWeldParamKey];
                if (!weldParams || typeof weldParams !== 'object') continue;

                // Extract header and values
                const headers = weldParams.header || [];
                const values = weldParams.values || {};
                const passValues = values.Pass || [];

                if (passValues.length === 0) continue;

                // Extract arrays from Pass values
                let passNames: string[] = [];
                let passPendNames: string[] = [];
                let passGroupNumbers: string[] = [];
                let endingAngles: string[] = [];

                for (const passItem of passValues) {
                    if (passItem['Pass Name'] && Array.isArray(passItem['Pass Name'])) {
                        passNames = passItem['Pass Name'];
                    }
                    if (passItem['Pass Pendant Name'] && Array.isArray(passItem['Pass Pendant Name'])) {
                        passPendNames = passItem['Pass Pendant Name'];
                    }
                    if (passItem['Pass Group Number'] && Array.isArray(passItem['Pass Group Number'])) {
                        passGroupNumbers = passItem['Pass Group Number'];
                    }
                    if (passItem['Ending Angle']) {
                        // Ending Angle can be an array where first element is {Unit: "deg"} and second is the values array
                        if (Array.isArray(passItem['Ending Angle'])) {
                            const endingAngleValue = passItem['Ending Angle'];
                            // Check if first element is an object with Unit property
                            if (endingAngleValue.length > 1 &&
                                typeof endingAngleValue[0] === 'object' &&
                                endingAngleValue[0]?.Unit &&
                                Array.isArray(endingAngleValue[1])) {
                                endingAngles = endingAngleValue[1];
                            } else {
                                endingAngles = endingAngleValue;
                            }
                        }
                    }
                }

                // Determine torch type (LEAD/TRAIL) from headers
                const torchMap = new Map<number, string>();
                headers.forEach((headerItem: any) => {
                    const headerKey = Object.keys(headerItem)[0];
                    if (headerKey) {
                        const parts = headerKey.split(':');
                        const groupNum = parts[0]?.trim();
                        const torchValues = headerItem[headerKey];
                        if (Array.isArray(torchValues) && torchValues.length >= 2) {
                            // Map indices: even = LEAD, odd = TRAIL
                            // But we need to track which pass index corresponds to which group
                            // For now, we'll use the pattern: even indices = LEAD, odd = TRAIL
                        }
                    }
                });

                // Create pass entries by iterating through the arrays
                const maxLength = Math.max(passNames.length, passPendNames.length, passGroupNumbers.length);

                for (let i = 0; i < maxLength; i++) {
                    const passName = passNames[i] || '';
                    const passPendName = passPendNames[i] || '';
                    const passGroupNumber = passGroupNumbers[i] || 'Unknown';
                    const endingAngle = endingAngles[i] || '';

                    // Determine torch: even indices are LEAD, odd are TRAIL (based on header structure)
                    // Each header entry has ["LEAD", "TRAIL"], so we need to map correctly
                    // For now, use index % 2 to determine LEAD (even) or TRAIL (odd)
                    const torch = i % 2 === 0 ? 'LEAD' : 'TRAIL';

                    params.push({
                        Type: 'PASS',
                        PassName: passName,
                        PassPendName: passPendName,
                        PassEnable: 'Yes',
                        PassGroupNumber: passGroupNumber,
                        EndingAngle: endingAngle,
                        Torch: torch,
                        originalIndex: i
                    });
                }
            }
        }

       // console.log('P625 RenamePass - Extracted passEntries:', params);
        return params;
    }, [jobData, pipeParamKey]);

    // Group passes by Group Number and determine torch type (LEAD or TRAIL)
    const groupedPasses = useMemo(() => {
        const grouped = new Map<string, { lead: PassData[]; trail: PassData[] }>();

        passEntries.forEach((pass: PassData) => {
            const groupKey = pass.PassGroupNumber || 'Unknown';

            if (!grouped.has(groupKey)) {
                grouped.set(groupKey, { lead: [], trail: [] });
            }

            const group = grouped.get(groupKey)!;

            // Use Torch field if available, otherwise try to infer
            const torch = (pass as any).Torch || pass.Torch || '';
            const torchLower = torch.toLowerCase();

            if (torchLower === 'lead') {
                group.lead.push(pass);
            } else if (torchLower === 'trail') {
                group.trail.push(pass);
            } else {
                // Fallback: try to infer from other fields
                const passName = pass.PassName || '';
                const passPendName = pass.PassPendName || '';

                if (passPendName.toLowerCase().includes('lead') || passName.toLowerCase().includes('lead')) {
                    group.lead.push(pass);
                } else if (passPendName.toLowerCase().includes('trail') || passName.toLowerCase().includes('trail')) {
                    group.trail.push(pass);
                } else {
                    // Default: alternate between lead and trail or put all in lead if no torch info
                    if (group.lead.length === 0 && group.trail.length === 0) {
                        group.lead.push(pass);
                    } else if (group.lead.length <= group.trail.length) {
                        group.lead.push(pass);
                    } else {
                        group.trail.push(pass);
                    }
                }
            }
        });

        return Array.from(grouped.entries()).sort((a, b) => {
            const aNum = parseInt(a[0]) || 0;
            const bNum = parseInt(b[0]) || 0;
            return aNum - bNum;
        });
    }, [passEntries]);

    // Format pass name for display (extract code and parenthetical value)
    const formatPassName = (passName: string, passPendName?: string) => {
        // Handle ".." as Disabled
        if (passName.trim() === '..' || passName.trim() === '.') {
            if (passPendName && passPendName.trim()) {
                return { code: 'Disabled', value: passPendName.trim(), display: `Disabled (${passPendName.trim()})` };
            }
            return { code: 'Disabled', value: '', display: 'Disabled ()' };
        }

        // Extract pattern like "F1T (1T)" or "Disabled (OP)"
        const match = passName.match(/^([^(]+)\s*\(([^)]+)\)$/);
        if (match) {
            return { code: match[1].trim(), value: match[2].trim(), display: `${match[1].trim()} (${match[2].trim()})` };
        }

        // If pass name doesn't have parentheses but we have PassPendName, use it
        if (passPendName && passPendName.trim()) {
            return { code: passName.trim(), value: passPendName.trim(), display: `${passName.trim()} (${passPendName.trim()})` };
        }

        // If no parentheses and no pendant name
        return { code: passName.trim(), value: '', display: passName.trim() };
    };

    const handleNewPassNameChange = (groupKey: string, torch: string, value: string) => {
        // Clear validation error and missing fields when user makes changes
        if (validationError) {
            setValidationError('');
        }

        const newMissingFields = new Set(missingFields);
        if (missingFields.has(`${groupKey}-${torch}-name`)) {
            newMissingFields.delete(`${groupKey}-${torch}-name`);
        }

        const entryKey = `${groupKey}-${torch}`;
        const current = renameEntries.get(entryKey) || {
            groupNo: groupKey,
            torch,
            passName: '',
            newPassName: '',
            numberToAppend: ''
        };

        const updated = {
            ...current,
            newPassName: value
        };

        const newEntries = new Map(renameEntries);
        newEntries.set(entryKey, updated);
        setRenameEntries(newEntries);

        // Update missing fields state
        if (newMissingFields.size !== missingFields.size) {
            setMissingFields(newMissingFields);
        }
    };

    const handleNumberChange = (groupKey: string, torch: string, value: string) => {
        // Clear validation error and missing fields when user makes changes
        if (validationError) {
            setValidationError('');
        }

        const newMissingFields = new Set(missingFields);

        const entryKey = `${groupKey}-${torch}`;
        const current = renameEntries.get(entryKey) || {
            groupNo: groupKey,
            torch,
            passName: '',
            newPassName: '',
            numberToAppend: ''
        };

        const updated = {
            ...current,
            numberToAppend: value.trim()
        };

        const newEntries = new Map(renameEntries);
        newEntries.set(entryKey, updated);
        setRenameEntries(newEntries);

        // Update missing fields state
        if (newMissingFields.size !== missingFields.size) {
            setMissingFields(newMissingFields);
        }
    };

    const handleApplyChanges = () => {
        if (!projectId || !jobData?.job_number) {
            console.error('Missing projectId or job_number');
            return;
        }

        if (isLoading) {
            return; // Prevent multiple clicks while loading
        }

        // Validate that all groups have pass names selected for both LEAD and TRAIL
        const missingGroups: string[] = [];
        groupedPasses.forEach(([groupKey, passes]) => {
            const leadEntry = renameEntries.get(`${groupKey}-lead`);
            const trailEntry = renameEntries.get(`${groupKey}-trail`);

            if (passes.lead && passes.lead.length > 0) {
                const hasPassName = leadEntry?.newPassName && leadEntry.newPassName.trim();
                if (!hasPassName) {
                    missingGroups.push(`${groupKey} (LEAD)`);
                }
            }
            if (passes.trail && passes.trail.length > 0) {
                const hasPassName = trailEntry?.newPassName && trailEntry.newPassName.trim();
                if (!hasPassName) {
                    missingGroups.push(`${groupKey} (TRAIL)`);
                }
            }
        });

        if (missingGroups.length > 0) {
            // Track which fields are missing for red border highlighting
            const missingFieldsSet = new Set<string>();
            groupedPasses.forEach(([groupKey, passes]) => {
                const leadEntry = renameEntries.get(`${groupKey}-lead`);
                const trailEntry = renameEntries.get(`${groupKey}-trail`);

                if (passes.lead && passes.lead.length > 0) {
                    const hasPassName = leadEntry?.newPassName && leadEntry.newPassName.trim();
                    if (!hasPassName) {
                        missingFieldsSet.add(`${groupKey}-lead-name`);
                    }
                }
                if (passes.trail && passes.trail.length > 0) {
                    const hasPassName = trailEntry?.newPassName && trailEntry.newPassName.trim();
                    if (!hasPassName) {
                        missingFieldsSet.add(`${groupKey}-trail-name`);
                    }
                }
            });
            setMissingFields(missingFieldsSet);

         Notification.warning('Please select pass name for all groups');
            return;
        }

        // Clear validation error and missing fields if validation passes
        setValidationError('');
        setMissingFields(new Set());

           // Build renamed_passes array from renameEntries
           const renamedPassesMap = new Map<string, any>(); // Use Map to prevent duplicates

        // Iterate through each rename entry (groupKey-torch format)
        renameEntries.forEach((entry, entryKey) => {
            if (!entry.newPassName) {
                return; // Skip if newPassName is missing
            }

            // Parse groupKey and torch from entryKey (e.g., "DA-lead" -> groupKey: "DA", torch: "lead")
            const parts = entryKey.split('-');
            const groupKey = parts[0] || entry.groupNo;
            const torch = parts[1] || entry.torch || 'lead';

            // Find the group data
            const groupData = groupedPasses.find(([key]) => key === groupKey);
            if (!groupData) return;

            const [, passes] = groupData;
            const torchPasses = torch.toLowerCase() === 'lead' ? passes.lead : passes.trail;

            // Create newPassName by combining selected name with number (e.g., "Root" + "1" = "Root 1")
            // Only append number if provided
            const newPassNameFull = entry.numberToAppend && entry.numberToAppend.trim()
                ? `${entry.newPassName} ${entry.numberToAppend}`
                : entry.newPassName + ' ';

            // Determine torch value (use actual torch ID from pass if available, otherwise use LEAD/TRAIL)
            const torchValue = torch.toLowerCase() === 'lead' ? 'LEAD' : 'TRAIL';

            // Add an entry for each pass in this group/torch combination
            torchPasses.forEach((pass, index) => {
                const formatted = formatPassName(pass.PassName || '', pass.PassPendName);
                // Try to get actual torch ID from pass, fallback to LEAD/TRAIL
                const torchId = pass.Torch || torchValue;
                const passOrderIndex = pass.originalIndex ?? index;

                // If pass is disabled, use original name; otherwise use the new selected name
                const isDisabled = pass.PassEnable && pass.PassEnable.toLowerCase() === 'no';
                const finalNewPassName = isDisabled ? pass.PassName : newPassNameFull;

                // Create unique key to prevent duplicates while keeping the original Pass Pendant Name index.
                const uniqueKey = `${groupKey}-${passOrderIndex}-${torchId}`;

                // Only add if not already in map (prevents duplicates)
                if (!renamedPassesMap.has(uniqueKey)) {
                    renamedPassesMap.set(uniqueKey, {
                        groupNumber: groupKey,
                        passIndex: passOrderIndex,
                        torch: torchId,
                        passName: formatted.display,
                        newPassName: finalNewPassName
                    });
                }
            });
        });

        if (renamedPassesMap.size === 0) {
            console.log('No passes to rename');
            return;
        }

        // Sort by the original Pass Pendant Name index so payload order matches the source JSON arrays.
        const renamedPasses = Array.from(renamedPassesMap.values()).sort((a, b) => {
            const indexA = a.passIndex ?? 0;
            const indexB = b.passIndex ?? 0;
            if (indexA !== indexB) return indexA - indexB;

            // If two records share an index, keep LEAD before TRAIL.
            if (a.torch === 'LEAD' && b.torch === 'TRAIL') return -1;
            if (a.torch === 'TRAIL' && b.torch === 'LEAD') return 1;
            return 0;
        }).map(({ passIndex, ...rest }) => rest); // Remove passIndex from final output

        

        // Build the payload
        const payload = {
            project_id: projectId,
            job_number: jobData.job_number,
            is_renamed: true,
            json_payload: {
                renamed_passes: renamedPasses
            }
        };

   //     console.log('Applying rename changes with payload:', payload);

        // Set loading state
        setIsLoading(true);

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Dispatch Redux action to call API
        dispatch(renamePass(payload) as any);

        // Reset loading after a reasonable timeout (saga will handle success/error notifications)
        timeoutRef.current = setTimeout(() => {
            setIsLoading(false);
            // Reset after applying only if loading completes
            setRenameEntries(new Map());
            // Call refresh callback to reload weld parameters
            if (onRenameSuccess) {
                onRenameSuccess();
            }
            timeoutRef.current = null;
        }, 3000); // 3 second timeout - adjust based on typical API response time
    };

    // Determine rowspan for group numbers
    const getRowspan = (groupKey: string) => {
        const group = groupedPasses.find(([key]) => key === groupKey);
        if (!group) return 1;
        const [, passes] = group;
        let count = 0;
        if (passes.lead && passes.lead.length > 0) count++;
        if (passes.trail && passes.trail.length > 0) count++;
        return count || 1;
    };

    return (
        <div className="flex flex-col h-full">
            {/* Sticky action toolbar at the top — scoped to Rename Pass tab */}
            <div className="flex items-center justify-end gap-3 px-6 py-3 bg-white border-b border-[#E2E5EA] flex-shrink-0">
                <Button
                    variant="outline"
                    onClick={() => setRenameEntries(new Map())}
                    disabled={isLoading}
                    className="h-9 px-4 border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                >
                    Reset
                </Button>
                <Button
                    onClick={handleApplyChanges}
                    disabled={isLoading}
                    className="h-9 px-4 bg-hero-red hover:bg-hero-red/90 text-white text-sm font-medium"
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Applying...
                        </>
                    ) : (
                        'Apply Changes'
                    )}
                </Button>
            </div>

            {/* Table */}
            <Card className="flex-1 flex flex-col overflow-hidden min-h-0 border-0 shadow-none rounded-none">
                <CardContent className="p-0 flex-1 flex flex-col overflow-hidden min-h-0">
                    <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        <div className="overflow-x-auto">
                            <Table className="w-full">
                                <TableHeader className="sticky top-0 z-10 bg-gray-100">
                                    <TableRow className="bg-gray-100">
                                        <TableHead className="w-24 text-center sticky left-0 bg-gray-100 z-20 border-r-2 border-gray-300">Group No</TableHead>
                                        <TableHead className="w-32 text-center">Torch</TableHead>
                                        <TableHead className="min-w-40">Pass Name</TableHead>
                                        <TableHead className="min-w-48">New Pass Name</TableHead>
                                        <TableHead className="min-w-40">Number to Append</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {groupedPasses.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-12 text-gray-500">
                                                <div className="flex flex-col items-center gap-2">
                                                    <p>No pass data available</p>
                                                    <p className="text-xs text-gray-400">Please check console for data extraction logs</p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        groupedPasses.map(([groupKey, passes]) => {
                                            const leadPasses = passes.lead || [];
                                            const trailPasses = passes.trail || [];
                                            const rowspan = getRowspan(groupKey);

                                            return (
                                                <React.Fragment key={groupKey}>
                                                    {leadPasses.length > 0 && (
                                                        <TableRow>
                                                            <TableCell
                                                                rowSpan={rowspan}
                                                                className="border-r-2 border-gray-300 text-center align-middle bg-gray-50 sticky left-0 z-10"
                                                            >
                                                                <span className="text-lg font-bold">{groupKey}</span>
                                                            </TableCell>
                                                            <TableCell className="text-center font-semibold bg-blue-50 align-top py-4">
                                                                LEAD
                                                            </TableCell>
                                                            <TableCell className="text-red-600 align-top py-4">
                                                                <ul className="list-none space-y-1 pl-0">
                                                                    {leadPasses.map((pass, idx) => {
                                                                        const formatted = formatPassName(pass.PassName || '', pass.PassPendName);
                                                                        return (
                                                                            <li key={idx} className="flex items-start">
                                                                                <span className="text-red-600 mr-2">•</span>
                                                                                <span className="font-mono">{formatted.display}</span>
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            </TableCell>
                                                            <TableCell className="align-top py-4">
                                                                <div>
                                                                    <Select
                                                                        value={renameEntries.get(`${groupKey}-lead`)?.newPassName || ''}
                                                                        onValueChange={(value) => handleNewPassNameChange(groupKey, 'lead', value)}
                                                                    >
                                                                        <SelectTrigger className={missingFields.has(`${groupKey}-lead-name`) && !renameEntries.get(`${groupKey}-lead`)?.newPassName ? ' border-2' : ''}>
                                                                            <SelectValue placeholder="Select Pass Name" />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            {passNameOptions.map((option) => (
                                                                                <SelectItem key={option.value} value={option.value}>
                                                                                    {option.label}
                                                                                </SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                    {missingFields.has(`${groupKey}-lead-name`) && !renameEntries.get(`${groupKey}-lead`)?.newPassName && (
                                                                        <p className="mt-1 text-xs text-red-500">Please select pass name</p>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="align-top py-4">
                                                                <div>
                                                                    <Input
                                                                        type="number"
                                                                        value={renameEntries.get(`${groupKey}-lead`)?.numberToAppend || ''}
                                                                        onChange={(e) => handleNumberChange(groupKey, 'lead', e.target.value)}
                                                                        min={1}
                                                                        max={20}
                                                                        placeholder="Number to Append (Optional)"
                                                                    />
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                    {trailPasses.length > 0 && (
                                                        <TableRow>
                                                            {leadPasses.length === 0 && (
                                                                <TableCell
                                                                    rowSpan={rowspan}
                                                                    className="border-r-2 border-gray-300 text-center align-middle bg-gray-50 sticky left-0 z-10"
                                                                >
                                                                    <span className="text-lg font-bold">{groupKey}</span>
                                                                </TableCell>
                                                            )}
                                                            <TableCell className="text-center font-semibold bg-orange-50 align-top py-4">
                                                                TRAIL
                                                            </TableCell>
                                                            <TableCell className="text-red-600 align-top py-4">
                                                                <ul className="list-none space-y-1 pl-0">
                                                                    {trailPasses.map((pass, idx) => {
                                                                        const formatted = formatPassName(pass.PassName || '', pass.PassPendName);
                                                                        return (
                                                                            <li key={idx} className="flex items-start">
                                                                                <span className="text-red-600 mr-2">•</span>
                                                                                <span className="font-mono">{formatted.display}</span>
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            </TableCell>
                                                            <TableCell className="align-top py-4">
                                                                <div>
                                                                    <Select
                                                                        value={renameEntries.get(`${groupKey}-trail`)?.newPassName || ''}
                                                                        onValueChange={(value) => handleNewPassNameChange(groupKey, 'trail', value)}
                                                                    >
                                                                        <SelectTrigger className={missingFields.has(`${groupKey}-trail-name`) && !renameEntries.get(`${groupKey}-trail`)?.newPassName ? ' border-2' : ''}>
                                                                            <SelectValue placeholder="Select Pass Name" />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            {passNameOptions.map((option) => (
                                                                                <SelectItem key={option.value} value={option.value}>
                                                                                    {option.label}
                                                                                </SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                    {missingFields.has(`${groupKey}-trail-name`) && !renameEntries.get(`${groupKey}-trail`)?.newPassName && (
                                                                        <p className="mt-1 text-xs text-red-500">Please select pass name</p>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="align-top py-4">
                                                                <div>
                                                                    <Input
                                                                        type="number"
                                                                        value={renameEntries.get(`${groupKey}-trail`)?.numberToAppend || ''}
                                                                        onChange={(e) => handleNumberChange(groupKey, 'trail', e.target.value)}
                                                                        min={1}
                                                                        max={20}
                                                                        placeholder="Number to Append (Optional)"
                                                                    />
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            );
                                        }))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                </CardContent>
            </Card>
        </div>
    );
};

export default RenamePass;
